import { Divider, Typography } from '@material-ui/core'

import { PriceRow } from '../../../../components/PriceRow'
import { getTranslate } from '../../../../localization'
import {
  BillingForBookingDetails,
  RoomStay
} from '../../../../types/bookingDetails'
import { PromotionApplication } from '../../../../types/cart'
import PromotionAllocationRows from '../../../BookingStepper/components/PromotionAllocationRows'
import { useStyles } from './styles/index.styles'

type Props = {
  adminEditMode: boolean
  billingInfo: BillingForBookingDetails | undefined
  promotionApplication?: PromotionApplication | null
  rooms?: RoomStay[]
}
const BillingSummary = ({
  adminEditMode,
  billingInfo,
  promotionApplication,
  rooms = []
}: Props) => {
  const classes = useStyles()
  const appliedPromotions = promotionApplication?.applied ?? []
  const hasPromotionDiscount = (billingInfo?.promotionDiscount ?? 0) > 0

  const allocationRoomLabel = (roomStayReference: string) => {
    const roomIndex = rooms.findIndex(
      (room) => room.reservationNumber === roomStayReference
    )
    const room = rooms[roomIndex]

    return room
      ? `${getTranslate('Room Stay')} ${roomIndex + 1} — ${
          room.roomTypeName
        } (${room.displayRatePlanCode})`
      : getTranslate('Room stay {reference}', {
          reference: roomStayReference
        })
  }

  return (
    <div
      className={[
        classes.container,
        adminEditMode ? classes.adminRoot : ''
      ].join(' ')}>
      <div className={classes.top}>
        <Typography variant="h4" className={classes.title}>
          {getTranslate('Final Billing Summary')}
        </Typography>
      </div>
      <Divider />
      <div className={classes.section}>
        {billingInfo?.roomsPrice.map((r, i) => (
          <PriceRow
            key={i}
            label={`${r.roomTypeName} (${r.displayRatePlanCode})`}
            value={r.price}
            currency={billingInfo?.curruncy}
          />
        ))}
        <PriceRow
          label={'Subtotal Before Discount'}
          value={billingInfo?.subTotalBeforeDiscount ?? 0}
          currency={billingInfo?.curruncy}
        />
      </div>
      <Divider />
      <div className={classes.section}>
        <PriceRow
          // 'Discount' alone read as the booking's only discount and sat at 0 beside a
          // non-zero promotion line. This one is the privileged-role percentage.
          label={getTranslate('Manual Discount')}
          value={billingInfo?.discount ?? 0}
          isDiscount
          currency={billingInfo?.curruncy}
        />
        {hasPromotionDiscount && appliedPromotions.length > 0 ? (
          appliedPromotions.map((promotion) => (
            <div key={promotion.promotionId}>
              <PriceRow
                label={promotion.name}
                translateLabel={false}
                minorAmount={promotion.discountMinor}
                currency={promotion.currency}
                isDiscount
              />
              <PromotionAllocationRows
                allocations={promotion.allocations}
                currency={promotion.currency}
                roomLabel={allocationRoomLabel}
              />
            </div>
          ))
        ) : hasPromotionDiscount ? (
          <PriceRow
            label={getTranslate('Promotion Discount')}
            value={billingInfo?.promotionDiscount ?? 0}
            isDiscount
            currency={billingInfo?.curruncy}
          />
        ) : null}
        <PriceRow
          label={getTranslate('Additional Discount Amount')}
          value={billingInfo?.additionalDiscount ?? 0}
          currency={billingInfo?.curruncy}
          isDiscount
        />
        <PriceRow
          label={'Subtotal After Discount'}
          value={billingInfo?.subTotalAfterDiscount ?? 0}
          currency={billingInfo?.curruncy}
        />
      </div>
      <Divider />
      <div className={classes.section}>
        <PriceRow
          label={'Amendment Penalty Amount'}
          value={billingInfo?.amendmentPenaltyAmount ?? 0}
          currency={billingInfo?.curruncy}
        />
        <PriceRow
          label={'Cancelation Penalty Amount'}
          value={billingInfo?.cancelationPenaltyAmount ?? 0}
          currency={billingInfo?.curruncy}
        />
        <PriceRow
          label={
            billingInfo?.refundAmount && billingInfo?.refundAmount > 0
              ? 'Refund Amount'
              : 'Payable After Penalty'
          }
          value={Math.abs(billingInfo?.refundAmount ?? 0)}
          currency={billingInfo?.curruncy}
        />
      </div>
      <Divider />
      <div className={classes.section}>
        <div className={classes.row}>
          <Typography variant="h6">
            {getTranslate('Reservation Method')}
          </Typography>
          <Typography variant="h6">{billingInfo?.reservationMethod}</Typography>
        </div>
        <PriceRow
          label={'Final Amount'}
          value={billingInfo?.finalAmount ?? 0}
          currency={billingInfo?.curruncy}
          bold
        />
      </div>
    </div>
  )
}

export default BillingSummary
